import { Formik, FormikProps } from "formik";
import React, { FC, memo } from "react";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { VALIDATION_TYPES } from "@/components/Formik";
import { IConsultantProfile, IConsultantProfileSkill } from "@/data/consultant-profiles";
import { Flex } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import { Messenger } from "@/views/components/Messenger";

import { profilePartialUpdaterBuilder } from "../profilePartialUpdaterBuilder";
import { IProfileFormikProps } from "../types";
import { FormikSkillsSection, validationSkillsSectionSchema } from "./SkillsSectionEdit";

export const validationSkillsFormSchema = validationSkillsSectionSchema;

export interface ISkillsFormSectionValues {
  skills: IConsultantProfileSkill[];
}

interface ISkillsFormSectionProps {
  form: FormikProps<ISkillsFormSectionValues>;
}

export const SKILLS_MESSENGER_CONTEXT = "profile-skills-form";

interface ISkillsFormComponentProps extends ISkillsFormSectionProps {
  source: IConsultantProfile;
}

interface ISkillsFormProps extends ISkillsFormComponentProps, IEditableSectionRenderProps {}

export const SkillsFormComponent: FC<ISkillsFormComponentProps> = memo(({ form, source }) => {
  return (
    <Flex direction="column" innerMargin="md">
      <Messenger context={SKILLS_MESSENGER_CONTEXT} />
      <Flex direction="column">
        <Flex direction="column" innerMargin="sm">
          <FormikSkillsSection
            name="skills"
            testName="skills"
            value={form.values.skills}
            profile={source}
            setFieldTouched={form.setFieldTouched}
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

export const SkillsForm: FC<ISkillsFormProps> = memo(props => {
  const { editableProps, form, source } = props;

  useEditableSubmit(form, editableProps);

  return (
    <FormikForm>
      <SkillsFormComponent form={form} source={source} />
    </FormikForm>
  );
});

export interface ISkillsFormikProps extends IProfileFormikProps<ISkillsFormSectionValues>, ISkillsFormProps {}

export const SkillsFormik: FC<ISkillsFormikProps> = memo(({ initialValues, validationSchema, onSubmit, ...rest }) => {
  const isPrivateProfile = !rest.source.companyRole;

  const skillsSchema = Yup.object().shape({
    skills: Yup.array().of(Yup.object()).min(1, VALIDATION_TYPES.ARRAY_MORE_THAN),
  });

  const finalValidationSchema = !isPrivateProfile ? validationSchema : validationSchema.concat(skillsSchema);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={finalValidationSchema}
      onSubmit={onSubmit}
      render={(form: FormikProps<ISkillsFormSectionValues>) => <SkillsForm {...rest} form={form} />}
    />
  );
});

export const ConsultantProfileSkillsEdit = profilePartialUpdaterBuilder(SkillsFormik, {
  validationSchema: validationSkillsFormSchema,
  messengerContext: SKILLS_MESSENGER_CONTEXT,
});
